import React, { useState } from 'react';
import {
  IconButton,
  Link,
  Stack,
  Text,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import clsx from 'clsx';
import { FolderExpand } from 'documents/documentAssignment/folder/list/FolderExpand';
import { useCommonStyles } from 'common/styles';
import { useStyles } from './index.styles';
import {
  TrainingFolders_trainingFolders,
  TrainingFolders_trainingFolders_nodes,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes,
} from '../__generated__/TrainingFolders';
import { LookupTrainingSearch_lookupTrainingSearch_nodes } from '../helpSearchBar/__generated__/LookupTrainingSearch';

// Make a unified type for recursion
type FolderNodeType =
  | TrainingFolders_trainingFolders_nodes
  | TrainingFolders_trainingFolders_nodes__childFolders_edges_node;

interface FolderNodeProps {
  node: FolderNodeType;
  level?: number;
  index: number;
  setTrainingFilesNodes: React.Dispatch<
    React.SetStateAction<
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes[]
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes[]
    >
  >;
  setSelectedNodeName: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedFolderId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedFolderId: string | undefined;
}

const INDENT_SIZE = 50;

function getChildNodes(
  node: FolderNodeType
): TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes[] {
  if (!node._childFolders) return [];
  return 'edges' in node._childFolders
    ? node._childFolders.edges.map((e) => e.node)
    : node._childFolders.nodes;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  node,
  setTrainingFilesNodes,
  setSelectedNodeName,
  setSelectedFolderId,
  selectedFolderId,
  level = 0,
  index,
}) => {
  const [isOpen, setIsOpen] = useState(index === 0 && !selectedFolderId);
  const marginLeft = INDENT_SIZE * level;
  const commonStyles = useCommonStyles();
  const styles = useStyles();
  const theme = useTheme();

  const childNodes = getChildNodes(node);

  const handleClick = () => {
    setIsOpen(!isOpen);
    setTrainingFilesNodes(node.trainingFiles?.nodes ?? []);
    setSelectedNodeName(node.name);
    setSelectedFolderId(node.id);
  };

  const textClass = clsx(
    node.id === selectedFolderId
      ? styles.selectedNodeStyle
      : level === 0
      ? commonStyles.semibold
      : styles.textStyle
  );

  return (
    <Stack
      style={{
        backgroundColor: isOpen ? theme.palette.neutralLighterAlt : undefined,
        paddingRight: 5,
      }}
    >
      <Link
        className={styles.linkHover}
        style={{ textDecoration: 'none' }}
        onClick={handleClick}
      >
        <Stack
          verticalAlign="center"
          horizontal
          tokens={{ childrenGap: 20 }}
          style={{ marginLeft }}
        >
          <TooltipHost content={node.hint ?? undefined}>
            <Stack
              horizontal
              tokens={{ childrenGap: 10 }}
              verticalAlign="center"
            >
              {!!level ? (
                <FolderExpand isOpen={isOpen} />
              ) : (
                <IconButton
                  iconProps={{
                    iconName: isOpen ? 'ChevronDownMed' : 'ChevronLeftMed',
                  }}
                  ariaLabel="Collapse"
                />
              )}
              <TooltipHost content={node.hint || undefined}>
                <Text nowrap block className={textClass} variant="medium">
                  {node?.name?.length && node?.name?.length > 30
                    ? node.name.slice(0, 27) + '…'
                    : node.name}
                </Text>
              </TooltipHost>
            </Stack>
          </TooltipHost>
        </Stack>
      </Link>

      {isOpen &&
        childNodes.map((child) => (
          <FolderNode
            key={child.id}
            node={child as FolderNodeType}
            level={level + 1}
            setTrainingFilesNodes={setTrainingFilesNodes}
            setSelectedNodeName={setSelectedNodeName}
            setSelectedFolderId={setSelectedFolderId}
            selectedFolderId={selectedFolderId}
            index={index}
          />
        ))}
    </Stack>
  );
};

interface FolderTreeProps {
  data: TrainingFolders_trainingFolders;
  setTrainingFilesNodes: React.Dispatch<
    React.SetStateAction<
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes[]
      | TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes[]
      | LookupTrainingSearch_lookupTrainingSearch_nodes[]
    >
  >;
  setSelectedNodeName: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedFolderId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedFolderId: string | undefined;
}

const FolderTree: React.FC<FolderTreeProps> = (props) => {
  const styles = useStyles();

  return (
    <Stack grow className={styles.leftPanelContainer}>
      {props.data.nodes.map((folder, i) => (
        <FolderNode key={folder.id} node={folder} index={i} {...props} />
      ))}
    </Stack>
  );
};

export default FolderTree;
